import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import SlidingBetslipView from './view/index.js';
import MojitoServices from 'mojito/services';
import SlidingBetslipTypes from './types/index.js';
import Betslip from 'modules/betslip/index.js';
import QuickBetslip from 'modules/quick-betslip/index.js';
import { set, noop } from 'mojito/utils';
import { connect } from 'react-redux';
import { actions as keypadActions } from 'modules/keypad/store/slice.js';

const { FlexPane } = MojitoPresentation.Components;
const intentActions = MojitoCore.Intents.actions;
const IntentTypes = MojitoPresentation.Base.Intent.Types;
const {
    actions: betslipActions,
    types: BetslipTypes,
    utils: BetslipUtils,
    selectors: { selectBetslipStatus, selectReceipt },
} = MojitoServices.Betslip;
const { actions: userSettingsActions, types: UserSettingsTypes } = MojitoServices.UserSettings;
const { BETSLIP_FREE_BET_HINT } = UserSettingsTypes.ONBOARDING_TYPES;
const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;
const { BETSLIP_TYPE } = SlidingBetslipTypes;
const isShallowEqual = MojitoCore.Base.objUtils.isShallowEqual;

class SlidingBetslipController extends UIViewImplementation {
    constructor(props) {
        super(props);

        this.onClose = this.onClose.bind(this);
        this.onSwipeUp = this.onSwipeUp.bind(this);
        this.onCloseSlidingBetslip = this.onCloseSlidingBetslip.bind(this);
        this.onClearReceipt = this.onClearReceipt.bind(this);
        this.onBetslipButtonClick = this.onBetslipButtonClick.bind(this);
    }

    componentDidUpdate(prevProps) {
        const { isOpen, betslipType, dispatch } = this.props;
        if (
            prevProps.betslipType === BETSLIP_TYPE.QUICK_BETSLIP &&
            betslipType === BETSLIP_TYPE.STANDARD_BETSLIP &&
            isOpen
        ) {
            dispatch(keypadActions.setIsHidden(true));
            dispatch(keypadActions.setInputId(undefined));
            this.emitAnalytics('quickBetslipToFull');
        }

        if (prevProps.isOpen !== isOpen) {
            const analyticsEvent = isOpen ? 'openBetslip' : 'closeBetslip';
            this.emitAnalytics(analyticsEvent, betslipType);
        }
    }

    componentDidMount() {
        this.props.dispatch(betslipActions.initBetslip());
    }

    componentWillUnmount() {
        this.props.dispatch(betslipActions.disposeBetslip());
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isShallowEqual(this.props, nextProps) || !isShallowEqual(this.state, nextState);
    }

    onClose() {
        const { dispatch, betslipStatus, isPending, hasReceipts } = this.props;
        // User shouldn't be able to close betslip when it is pending
        // or when overask is in progress.
        const isOverask = BetslipUtils.isOverask(betslipStatus);
        const isFailed = BetslipUtils.isFailed(betslipStatus);

        if (isFailed) {
            this.clearBetslip();
        }

        if (!isPending && !isOverask) {
            dispatch(intentActions.publishIntent(IntentTypes.HIDE_SLIDING_BETSLIP));
            if (hasReceipts) {
                this.clearBetslip();
            }
            this.props.onClose();
        }
    }

    clearBetslip() {
        this.props.dispatch(betslipActions.clearBetslip([BetslipTypes.BETSLIP_PART.SETTINGS]));
    }

    onClearReceipt() {
        this.props.dispatch(intentActions.publishIntent(IntentTypes.HIDE_SLIDING_BETSLIP));
    }

    onBetslipButtonClick() {
        const { dispatch, isOpen, hasReceipts, betslipStatus } = this.props;
        const isClosed = !isOpen;
        const uncommitedQBS = this.isQuickBetslipOpened() && !hasReceipts;
        if (isClosed || uncommitedQBS) {
            const isOverask = BetslipUtils.isOverask(betslipStatus);
            // If betslip is in overask state, we will always open standard betslip as QBS has no all needed information.
            const data = isOverask ? { betslipType: BETSLIP_TYPE.STANDARD_BETSLIP } : undefined;
            dispatch(intentActions.publishIntent(IntentTypes.SHOW_SLIDING_BETSLIP, data));
        } else {
            this.onClose();
        }
    }

    isQuickBetslip() {
        return BETSLIP_TYPE.QUICK_BETSLIP === this.props.betslipType;
    }

    renderBetslip() {
        if (!this.props.isOpen) {
            return null;
        }

        return (
            <FlexPane config={this.config.container}>
                {this.isQuickBetslip() ? (
                    <QuickBetslip onClose={this.onClose} />
                ) : (
                    <Betslip
                        config={this.config.betslipModule}
                        onClearReceipt={this.onClearReceipt}
                    />
                )}
            </FlexPane>
        );
    }

    getQuickBetslipStyle() {
        const { pendingQuickBetslip, openedQuickBetslip } = this.style;
        return this.props.isPending ? pendingQuickBetslip : openedQuickBetslip;
    }

    isQuickBetslipOpened() {
        return this.isQuickBetslip() && this.props.isOpen;
    }

    onCloseSlidingBetslip() {
        const { dispatch, hasReceipts } = this.props;

        if (this.isQuickBetslip()) {
            dispatch(
                userSettingsActions.updateOnboardingProgress({
                    [BETSLIP_FREE_BET_HINT]: { passed: true },
                })
            );
            dispatch(keypadActions.setIsHidden(true));
            dispatch(keypadActions.setInputId(undefined));
            hasReceipts && this.clearBetslip();
        }
    }

    onSwipeUp() {
        this.props.dispatch(intentActions.publishIntent(IntentTypes.SHOW_SLIDING_BETSLIP));
    }

    render() {
        const config = this.isQuickBetslipOpened()
            ? this.getQuickBetslipStyle()
            : this.config[this.props.betslipType];
        const { hasReceipts, isPending, isOpen } = this.props;
        return (
            <SlidingBetslipView
                config={config.view}
                onBetslipButtonClick={this.onBetslipButtonClick}
                onCloseClick={this.onClose}
                onClickOutside={!this.isQuickBetslip() || hasReceipts ? this.onClose : noop}
                onClosed={this.onCloseSlidingBetslip}
                onSwipeDown={this.onClose}
                onSwipeUp={this.onSwipeUp}
                isBetslipButtonMinimized={this.props.isBetslipButtonMinimized}
                isBetslipButtonCommitted={hasReceipts}
                isBetslipButtonDisabled={isPending || (isOpen && !this.isQuickBetslip())}
                isQuickBetslip={this.isQuickBetslip()}
                isOpen={isOpen}
                enableFreebetsIndication={this.isQuickBetslipOpened()}
            >
                {this.renderBetslip()}
            </SlidingBetslipView>
        );
    }
}

SlidingBetslipController.getStyle = (config, mode, merge) => {
    const quickBetslipView = config.quickBetslip.view;
    const disablePointerEvents = set(
        {},
        'slidingPane.overlay.style.container.pointerEvents',
        'none'
    );
    const enablePointerEvents = set(
        {},
        'slidingPane.overlay.style.container.pointerEvents',
        'auto'
    );
    const pendingQuickBetslipView = set(
        enablePointerEvents,
        'slidingPane.overlay.disableBodyScrolling',
        true
    );
    return {
        openedQuickBetslip: {
            view: merge(quickBetslipView, disablePointerEvents),
        },
        pendingQuickBetslip: {
            view: merge(quickBetslipView, pendingQuickBetslipView),
        },
    };
};

const mapStateToProps = state => ({
    isPending: BetslipUtils.isPending(selectBetslipStatus(state)),
    hasReceipts: !!selectReceipt(state),
    betslipStatus: selectBetslipStatus(state),
});

export default connect(mapStateToProps)(SlidingBetslipController);
